import { Link, NavLink } from 'react-router-dom';

import { useCan } from '../../stores/session';

/**
 * Navigation between the admin pages.
 *
 * They had none: `/admin/audit` was reachable only by typing it, which is how a
 * moderation surface ends up unused by the people it was built for.
 *
 * Each link is gated on the permission its page requires, so a moderator who
 * can see reports but not the audit log is offered exactly what they can open -
 * rather than a link that renders a "you cannot see this" page.
 *
 * ## Grouped, the way the work is
 *
 * Seventeen links in one row was a row nobody could scan. They fall into the
 * jobs people come here to do: the queues somebody moderates, what the site
 * says for itself, what the product is, and the money. A group only shows
 * when at least one of its links does.
 *
 * ## Two shapes
 *
 * A sidebar on a wide window, where it is a fixed rail beside the content;
 * a scrolling strip across the top on a narrow one, where a rail would take
 * the width the tables need.
 */
export function AdminNav({ shape }: { shape: 'rail' | 'strip' }) {
  const canReports = useCan('report.view');
  const canSafety = useCan('safety.view');
  const canAnnounce = useCan('announcement.post');
  const canFeature = useCan('featured.set');
  const canDmca = useCan('dmca.view');
  const canHosts = useCan('host.review');
  const canAudit = useCan('audit.view');
  const canFeatures = useCan('feature.manage');
  const canPayments = useCan('payments.manage');
  const canAds = useCan('ads.manage');
  const canTraffic = useCan('analytics.view');
  const canMastering = useCan('mastering.manage');

  const groups: { title: string | null; items: { to: string; label: string; end?: boolean; show: boolean }[] }[] = [
    { title: null, items: [{ to: '/admin', label: 'Overview', end: true, show: true }] },
    {
      title: 'Moderation',
      items: [
        { to: '/admin/users', label: 'Users', show: true },
        { to: '/admin/reports', label: 'Reports', show: canReports },
        // Beside Reports because they answer the same worry from opposite
        // directions - what people told us, and what nobody did.
        { to: '/admin/safety', label: 'Safety', show: canSafety },
        { to: '/admin/dmca', label: 'Claims', show: canDmca },
        { to: '/admin/host-applications', label: 'Hosts', show: canHosts },
        { to: '/admin/audit', label: 'Audit log', show: canAudit },
      ],
    },
    {
      // What the site says for itself: a different job from the queues above.
      title: 'Editorial',
      items: [
        { to: '/admin/announcements', label: 'Announcements', show: canAnnounce },
        { to: '/admin/featured', label: 'Featured', show: canFeature },
      ],
    },
    {
      title: 'Product',
      items: [
        // Gated on its own permission rather than on `admin.access`: switching
        // a feature on reaches every user at once, which is outside a
        // moderator's remit of reports, takedowns and suspensions.
        { to: '/admin/features', label: 'Features', show: canFeatures },
        // Only `admin.access`: it reads nothing and writes nothing.
        { to: '/admin/characters', label: 'Characters', show: true },
        { to: '/admin/mastering', label: 'Mastering', show: canMastering },
      ],
    },
    {
      // Money and where readers come from: commercial information about the
      // business rather than operational numbers about a queue, each on its
      // own key because each is a smaller circle of people.
      title: 'Business',
      items: [
        { to: '/admin/payments', label: 'Payments', show: canPayments },
        { to: '/admin/ads', label: 'Sponsorships', show: canAds },
        { to: '/admin/traffic', label: 'Traffic', show: canTraffic },
        { to: '/admin/ir8', label: 'IR8', show: canTraffic },
      ],
    },
  ];

  const visible = groups.map((g) => ({ ...g, items: g.items.filter((i) => i.show) })).filter((g) => g.items.length > 0);

  if (shape === 'strip') {
    return (
      <nav className="mb-1 flex gap-1 overflow-x-auto pb-3 [scrollbar-width:none]" aria-label="Admin sections" data-admin-nav="strip">
        {visible.flatMap((g) => g.items).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end ?? false}
            className={({ isActive }) =>
              `shrink-0 rounded-[var(--radius-pill)] px-3.5 py-1.5 text-sm whitespace-nowrap transition-colors ${
                isActive ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    );
  }

  return (
    <nav className="flex h-full flex-col" aria-label="Admin sections" data-admin-nav="rail">
      <div className="flex items-center gap-2 px-5 pt-6 pb-4">
        <span aria-hidden className="h-2 w-2 rounded-full bg-fresh" />
        <span className="meta-caps text-faint">Admin</span>
      </div>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 pb-6">
        {visible.map((group) => (
          <div key={group.title ?? 'top'} className="flex flex-col gap-0.5">
            {group.title && <span className="meta-caps mb-1.5 px-2 text-ghost">{group.title}</span>}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                // `end` on the index route only, or /admin stays highlighted
                // on every page beneath it.
                end={item.end ?? false}
                className={({ isActive }) =>
                  `relative flex items-center rounded-[var(--radius-control)] px-2.5 py-1.5 text-[13px] transition-colors ${
                    isActive
                      ? 'bg-raised font-medium text-ink before:absolute before:top-1.5 before:bottom-1.5 before:-left-3 before:w-0.5 before:rounded-full before:bg-accent'
                      : 'text-soft hover:bg-raised/60 hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="border-t border-line px-5 py-4">
        <Link to="/" className="text-[13px] text-faint transition-colors hover:text-ink">
          &larr; Back to the site
        </Link>
      </div>
    </nav>
  );
}
